import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemeToggle } from '../ThemeToggle';
import './Layout.css';
import LoadingBar from '../ui/LoadingBar';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationService from '../../services/NotificationService';
import { apiConfig } from '../../config/apiConfig';
import api from '../../services/apiService';
import { fetchWithAuth } from '../../utils/api';
import { UserAvatar } from '../UserAvatar';

// Icons
import { 
  Settings, 
  Users, 
  FileText, 
  BarChart2, 
  LogOut, 
  X, 
  Bell,
  HelpCircle,
  Clock,
  AlertTriangle,
  BookOpen,
  Trophy,
  Calendar,
  Sun,
  Moon,
  Laptop,
  LayoutGrid,
  User,
  Check,
  ChevronRight
} from 'lucide-react';
import LogoSVG from '../ui/LogoSVG';

// Custom Hamburger Icon using a single div element
const HamburgerIcon = () => {
  const { theme } = useTheme();
  return <div className={`menu-icon ${theme}`}></div>;
};

type MainLayoutProps = {
  children: ReactNode;
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('is_admin') === 'true';
  const username = localStorage.getItem('username') || 'User';
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // For page transitions
  const [dropdownNotifications, setDropdownNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const { unreadCount, markAsRead } = useNotifications();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Track mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside as any);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as any);
    };
  }, [profileMenuRef, notificationsRef]);
  
  // Show loading bar on route changes
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (notificationsOpen) {
      setLoadingNotifications(true);
      // Fetch only 3 most recent notifications for the dropdown
      NotificationService.getUserNotifications(Date.now(), 3)
        .then(data => {
          if (data && Array.isArray(data)) {
            setDropdownNotifications(data);
          } else {
            setDropdownNotifications([]);
          }
        })
        .catch(error => {
          console.error("Error fetching notifications:", error);
          setDropdownNotifications([]);
        })
        .finally(() => {
          setLoadingNotifications(false);
        });
    }
  }, [notificationsOpen]);

  // Skip layout for Home and Login pages
  if (location.pathname === '/' || location.pathname === '/login') {
    return <>{children}</>;
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Toggle profile menu - on click only
  const toggleProfileMenu = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  // Toggle notifications
  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
  };

  // Navigation link highlighting
  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  // Animation variants
  const sidebarVariants = {
    open: {
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    },
    closed: {
      x: '-100%',
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    }
  };

  // Navigation items with improved organization
  const navItems = [
    { 
      name: 'Dashboard', 
      icon: <LayoutGrid size={20} />, 
      path: '/dashboard', 
      adminOnly: false,
      category: 'main'
    },
    { 
      name: 'Motion Gallery', 
      icon: <BookOpen size={20} />, 
      path: '/motions/glossary', 
      adminOnly: false,
      category: 'main'
    },
    { 
      name: 'Practice Materials', 
      icon: <FileText size={20} />, 
      path: '/practice-materials', 
      adminOnly: false,
      category: 'main'
    },
    { 
      name: 'User Guide', 
      icon: <HelpCircle size={20} />, 
      path: '/user-guide', 
      adminOnly: false,
      category: 'main'
    }
  ];

  // Group items by category for better organization
  const getNavItemsByCategory = (category: string) => {
    return navItems.filter(item => 
      item.category === category && 
      (!item.adminOnly || (item.adminOnly && isAdmin))
    );
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Define the logout endpoints to try, in order of preference
        const logoutEndpoints = [
          '/api/logout-api/',
          '/api/auth/logout/',
          '/auth/logout/'
        ];
        
        let logoutSuccess = false;
        
        // Try each endpoint until one works
        for (const endpoint of logoutEndpoints) {
          try {
            console.log(`Attempting logout with endpoint: ${endpoint}`);
            
            // Use axios API instance that's properly configured with baseURL
            const response = await api.post(endpoint, { token });
            
            if (response.status === 200) {
              logoutSuccess = true;
              console.log(`Successfully logged out via ${endpoint}`);
              break;
            }
          } catch (error) {
            console.warn(`Logout failed with endpoint ${endpoint}:`, error);
            // Continue trying other endpoints
          }
        }
        
        if (!logoutSuccess) {
          // If API calls fail, attempt a direct token deletion
          try {
            console.log('Attempting token deletion via dedicated endpoint');
            await api.delete('/api/auth/token/', {
              data: { token } // Send token in request body for DELETE request
            });
            console.log('Successfully deleted token via direct API call');
            logoutSuccess = true;
          } catch (tokenError) {
            console.warn('Direct token deletion failed, trying alternative endpoint');
            try {
              await api.post('/auth/token/', { token });
              console.log('Successfully deleted token via alternative endpoint');
              logoutSuccess = true;
            } catch (altError) {
              console.warn('Alternative endpoint also failed:', altError);
            }
          }
        }
        
        // Log whether we were successful
        if (!logoutSuccess) {
          console.warn('All logout endpoints failed, proceeding with client-side logout');
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage and redirect regardless of API success
      localStorage.removeItem('token');
      localStorage.removeItem('is_admin');
      localStorage.removeItem('username');
      localStorage.removeItem('userId');
      window.location.href = '/login';
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      try {
        // Try to mark as read using the notification context
        const success = await markAsRead(notification.id);
        
        if (!success) {
          console.warn("Failed to mark notification as read using context, trying direct service call");
          // Fallback to direct service call
          const directSuccess = await NotificationService.markAsRead(notification.id);
          
          if (!directSuccess) {
            console.warn("Failed to mark notification as read, updating UI only");
            // Update local UI state to show as read even if API call failed
            setDropdownNotifications(prev => 
              prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
            );
          }
        }
      } catch (error) {
        console.error("Error marking notification as read:", error);
        // Still update UI to avoid confusion
        setDropdownNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
      }
    }
    
    if (notification.link) {
      navigate(notification.link);
    }
    
    setNotificationsOpen(false);
  };

  // Format notification time
  const formatNotificationTime = (timeStr: string) => {
    if (!timeStr) return 'Just now';
    
    // If it already has a formatted time (like "2 hours ago")
    if (timeStr.includes('ago') || timeStr.includes('now')) {
      return timeStr;
    }
    
    try {
      const date = new Date(timeStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch (e) {
      return 'Recently';
    }
  };

  return (
    <div className={`min-h-screen flex flex-col theme-transition ${theme === 'dark' ? 'dark' : 'light'}`}>
      <LoadingBar isLoading={isLoading} />
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-4 py-2">
          {/* Left section - Logo and Menu Toggle */}
          <div className="flex items-center">
            <button 
              onClick={toggleSidebar} 
              className="bg-transparent border-none p-0 m-2 focus:outline-none hover:bg-transparent md:hidden"
              style={{ background: 'none', boxShadow: 'none' }}
              aria-label="Toggle menu"
            >
              <HamburgerIcon />
            </button>
            <div className="logo-container ml-2">
              <Link to="/dashboard">
                <LogoSVG height={52} width={145} />
              </Link>
            </div>
          </div>
          
          <div className="navbar-right">
            {/* Notifications */}
            <div className="nav-item notification-center" ref={notificationsRef}>
              <button 
                onClick={toggleNotifications}
                className="notification-button"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="notification-indicator">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
              
              {notificationsOpen && (
                <div 
                  className="notification-dropdown" 
                  style={{ 
                    right: isMobile ? 'auto' : 0,
                    left: isMobile ? '50%' : 'auto',
                    transform: isMobile ? 'translateX(-50%)' : 'none',
                    maxWidth: isMobile ? '90vw' : '320px',
                    width: isMobile ? '90vw' : '320px',
                    animation: 'fadeIn 0.2s ease-in-out'
                  }}
                >
                  <div className="notification-header">
                    <h3>Notifications</h3>
                  </div>
                  <div className="notification-list">
                    {loadingNotifications ? (
                      <div className="notification-item loading">
                        <div className="loading-spinner"></div>
                        <p>Loading notifications...</p>
                      </div>
                    ) : dropdownNotifications.length > 0 ? (
                      dropdownNotifications.map(notification => (
                        <div 
                          key={notification.id} 
                          className={`notification-item ${!notification.read ? 'unread' : ''}`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="notification-content">
                            <p className="notification-message">{notification.message}</p>
                            <span className="notification-time">
                              {formatNotificationTime(notification.time || notification.created_at)}
                            </span>
                          </div>
                          {!notification.read && <div className="notification-dot"></div>}
                          <ChevronRight size={16} className="notification-icon" />
                        </div>
                      ))
                    ) : (
                      <div className="notification-item">
                        <p>No new notifications</p>
                      </div>
                    )}
                  </div>
                  <div className="notification-footer">
                    <Link to="/notifications" onClick={() => setNotificationsOpen(false)}>
                      View all notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* Theme Toggle */}
            <div className="nav-item theme-toggle-container">
              <ThemeToggle />
            </div>
            
            {/* User Profile */}
            <div className="nav-item profile-dropdown-container" ref={profileMenuRef}>
              <button 
                onClick={toggleProfileMenu} 
                className="profile-button"
              >
                <UserAvatar username={username} size="sm" />
              </button>
              
              {profileMenuOpen && (
                <div className="profile-dropdown">
                  <div className="profile-header">
                    <div className="profile-info">
                      <UserAvatar username={username} size="md" />
                      <div className="profile-name">
                        <h4>{username}</h4>
                        <span className="profile-role">{isAdmin ? 'Admin' : 'User'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="profile-menu">
                    <Link
                      to="/account-settings"
                      state={{ activeTab: "profile" }}
                      className="profile-menu-item"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <div className="menu-item-content">
                        <User size={16} className="mr-2" />
                        My Profile
                      </div>
                    </Link>
                    <Link
                      to="/account-settings"
                      className="profile-menu-item"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <div className="menu-item-content">
                        <Settings size={16} className="mr-2" />
                        Settings
                      </div>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="profile-menu-item logout"
                    >
                      <div className="menu-item-content">
                        <LogOut size={16} className="mr-2" />
                        Logout
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <div className="layout-container mt-16">
        {/* Sidebar overlay for mobile - closes sidebar when clicked */}
        <div 
          className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`} 
          onClick={() => setSidebarOpen(false)}
        ></div>
        
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <ul className="sidebar-menu">
            {navItems.map((item) => (
              !item.adminOnly || (item.adminOnly && isAdmin) ? (
                <li key={item.path}>
                  <Link 
                    to={item.path} 
                    className={isActivePath(item.path) ? 'active' : ''}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                  </Link>
                </li>
              ) : null
            ))}
          </ul>
        </aside>
        
        {/* Main Content */}
        <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout; 